/**
 * ノード詳細 `/nodes/$nodeId`。
 *
 * 運用でここを開く理由は 3 つしかない:
 *   1. 落ちているか（displayStatus / ハートビート / サービス）
 *   2. 詰まっているか（キュー滞留 / ディスク）
 *   3. 設定が届いているか（desired generation と applied generation の差）
 * タブの並びもその順にする。
 */
import * as React from 'react';
import { Link, getRouteApi } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  Cpu,
  Database,
  FileCode2,
  Fingerprint,
  Globe,
  KeyRound,
  Layers,
  ListChecks,
  PackageOpen,
  RefreshCw,
  RotateCcw,
  Send,
  ShieldAlert,
  ShieldOff,
  Terminal,
  Trash2,
  Wrench,
} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  CursorPagination,
  DataTable,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  KeyValue,
  NodeStatusBadge,
  PageHeader,
  Section,
  StatCard,
  StatusBadge,
  StatusDot,
  SwitchRow,
  Textarea,
  UsageBar,
  formatBytes,
  formatCount,
  formatDateTime,
  formatRelative,
  type DataTableColumn,
} from '@mail-sv/ui';
import { NODE_COMMAND_KIND, labelOf, toneOf } from '@mail-sv/shared';
import { api } from '../lib/api.js';
import { qk } from '../lib/query-keys.js';
import { daysUntil, formatUptime, ratioOf } from '../lib/format.js';
import { useCursorList } from '../lib/use-cursor-list.js';
import { OPS_PERMISSION } from '../lib/permissions.js';
import { toNodeDetail, type ApiNode, type ApiNodeDetail } from '../lib/node-view.js';
import { usePermission } from '../stores/auth.js';
import { PageTabs } from '../components/page-tabs.js';
import { JsonBlock } from '../components/code-block.js';
import { ConfirmTextDialog, FormDialog, SecretRevealDialog } from '../components/dialogs.js';
import { Field, NativeSelect } from '../components/form-fields.js';
import { LineChart, type Series } from '../components/spark-chart.js';
import { DetailSkeleton, ListEmpty, QueryError, RefetchingDot } from '../components/states.js';
import { RID } from './ids.js';
import type {
  BlocklistEntry,
  CertificateEntry,
  DomainSummary,
  NodeCommandRecord,
  NodeDetail,
  NodeEnrollTokenResult,
  NodeMetricHourly,
  NodeMetricsResponse,
  NodeObservedCertificate,
  NodeServiceState,
} from '../types/api.js';

const routeApi = getRouteApi(RID.nodeDetail);

export function NodeDetailPage(): React.ReactElement {
  const { nodeId } = routeApi.useParams();
  const search = routeApi.useSearch();
  const navigate = routeApi.useNavigate();
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const query = useQuery({
    queryKey: qk.node(nodeId),
    // API の DTO を境界（lib/node-view.ts）で画面の語彙へ変換してからキャッシュする。
    queryFn: async () => toNodeDetail(await api.get<ApiNodeDetail>(`/nodes/${nodeId}`)),
    refetchInterval: search.tab === 'overview' ? 10_000 : false,
  });

  const node = query.data;

  return (
    <>
      <PageHeader
        title={node?.hostname ?? 'ノード'}
        description={node ? `${node.region} / ${labelOf(node.role)} / ${node.publicIp ?? 'IP 未設定'}` : undefined}
        breadcrumbs={[{ label: 'サーバーノード', href: '/nodes' }, { label: node?.hostname ?? '…' }]}
        actions={
          <>
            <RefetchingDot active={query.isFetching && !query.isLoading} />
            {node ? <NodeStatusBadge displayStatus={node.displayStatus} /> : null}
            {canCommand && node ? <SendCommandButton nodeId={node.id} hostname={node.hostname} /> : null}
            {node ? <NodeOperationsMenu node={node} /> : null}
          </>
        }
      />

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.isLoading ? <DetailSkeleton /> : null}

      {node ? (
        <>
          <FingerprintBanner node={node} />
          <DriftBanner node={node} />

          <PageTabs
            value={search.tab}
            onChange={(tab) => void navigate({ search: (prev) => ({ ...prev, tab }) })}
            tabs={[
              { value: 'overview', label: '概要', icon: Activity },
              { value: 'services', label: 'サービス', icon: Cpu },
              { value: 'domains', label: '収容ドメイン', icon: Globe },
              { value: 'metrics', label: 'メトリクス', icon: Layers },
              { value: 'config', label: '設定', icon: FileCode2 },
              { value: 'commands', label: 'コマンド履歴', icon: Terminal },
              { value: 'updates', label: 'アップデート', icon: ListChecks },
            ]}
          />

          {search.tab === 'overview' ? <OverviewTab node={node} /> : null}
          {search.tab === 'services' ? <ServicesTab services={node.services} /> : null}
          {search.tab === 'domains' ? <DomainsTab nodeId={node.id} /> : null}
          {search.tab === 'metrics' ? (
            <MetricsTab
              nodeId={node.id}
              range={search.range}
              onRangeChange={(range) => void navigate({ search: (prev) => ({ ...prev, range }) })}
            />
          ) : null}
          {search.tab === 'config' ? <ConfigTab node={node} /> : null}
          {search.tab === 'commands' ? <CommandsTab nodeId={node.id} /> : null}
          {search.tab === 'updates' ? <UpdatesTab node={node} /> : null}
        </>
      ) : null}
    </>
  );
}

/* ------------------------------------------------------------ drift 警告 */

function DriftBanner({ node }: { node: NodeDetail }): React.ReactElement | null {
  const behind = node.appliedGeneration !== null && node.appliedGeneration < node.desiredGeneration;
  const drifted = node.driftSections.length > 0;
  if (!behind && !drifted) return null;

  return (
    <Alert variant={drifted ? 'destructive' : 'warning'} className="mb-5">
      <AlertTitle>{drifted ? '設定に差分があります' : '設定が未反映です'}</AlertTitle>
      <AlertDescription className="space-y-1">
        <p>
          配布世代 <span className="tabular">{node.desiredGeneration}</span> に対し、適用世代は{' '}
          <span className="tabular">{node.appliedGeneration ?? '未報告'}</span> です。
        </p>
        {drifted ? (
          <p>
            差分のあるセクション: <code className="font-mono">{node.driftSections.join(', ')}</code>
          </p>
        ) : null}
      </AlertDescription>
    </Alert>
  );
}

/* ---------------------------------------------------------------- overview */

function OverviewTab({ node }: { node: NodeDetail }): React.ReactElement {
  const m = node.metrics;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="CPU"
          value={m.cpuPercent === null ? '—' : `${Math.round(m.cpuPercent)}`}
          unit="%"
          icon={Cpu}
          tone={(m.cpuPercent ?? 0) >= 90 ? 'destructive' : (m.cpuPercent ?? 0) >= 70 ? 'warning' : 'default'}
        />
        <StatCard
          label="メモリ"
          value={formatBytes(m.memoryUsedBytes)}
          hint={m.memoryTotalBytes ? `全体 ${formatBytes(m.memoryTotalBytes)}` : undefined}
          icon={Database}
        />
        <StatCard
          label="ディスク"
          value={formatBytes(m.diskUsedBytes)}
          hint={`全体 ${formatBytes(m.diskTotalBytes)}`}
          icon={Database}
          tone={(ratioOf(m.diskUsedBytes, m.diskTotalBytes) ?? 0) >= 0.9 ? 'destructive' : 'default'}
        />
        <StatCard
          label="メールキュー滞留"
          value={formatCount(m.queueDepth)}
          icon={Send}
          tone={m.queueDepth > 1000 ? 'destructive' : m.queueDepth > 100 ? 'warning' : 'default'}
        />
      </div>

      <Section title="基本情報">
        <div className="rounded-lg border border-border bg-card p-4">
          <KeyValue
            columns={3}
            items={[
              { key: 'ホスト名', value: node.hostname },
              { key: '公開 IPv4', value: node.publicIp ?? '—' },
              { key: 'IPv6', value: node.ipv6 ?? '—' },
              { key: 'リージョン', value: node.region },
              { key: '役割', value: labelOf(node.role) },
              { key: 'リング', value: labelOf(node.ring) },
              { key: 'OS', value: node.osVersion ?? '—' },
              { key: 'エージェント版', value: node.agentVersion ?? '—' },
              { key: 'スタック版', value: node.stackVersion ?? '—' },
              { key: '配布世代', value: <span className="tabular">{node.desiredGeneration}</span> },
              { key: '適用世代', value: <span className="tabular">{node.appliedGeneration ?? '未報告'}</span> },
              { key: '設定状態', value: <StatusBadge status={node.applyState} size="sm" /> },
              { key: '最終疎通', value: `${formatRelative(node.lastSeenAt)}（${formatDateTime(node.lastSeenAt)}）` },
              { key: 'ハートビート閾値', value: `${node.heartbeatTimeoutSec} 秒` },
              { key: '設置日', value: formatDateTime(node.createdAt) },
              { key: '新規収容', value: node.acceptingNewMailboxes ? '受け入れ中' : '停止中' },
              { key: 'メンテナンス', value: node.maintenanceMode ? '有効' : '無効' },
              { key: 'ノードグループ', value: node.groupName ?? '—' },
            ]}
          />
        </div>
      </Section>

      <Section title="収容状況">
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-border bg-card p-4 sm:grid-cols-2">
          <UsageBar
            used={node.mailboxCount}
            total={node.capacity.maxMailboxes}
            label="メールボックス"
            formatValue={(v) => formatCount(v)}
          />
          <UsageBar
            used={node.metrics.diskUsedBytes}
            total={node.capacity.maxStorageBytes}
            label="ストレージ"
            formatValue={(v) => formatBytes(v)}
          />
        </div>
      </Section>

      <Section title="サービス">
        <ServiceList services={node.services} />
      </Section>
    </div>
  );
}

/* ---------------------------------------------------------------- services */

function ServicesTab({ services }: { services: NodeServiceState[] }): React.ReactElement {
  return (
    <Section
      title="サービス状態"
      description="再起動は shim に動詞が無いため提供しない。反映は reload かアップデート適用で行う（§4.5）。"
    >
      <ServiceList services={services} detailed />
    </Section>
  );
}

function ServiceList({
  services,
  detailed = false,
}: {
  services: NodeServiceState[];
  detailed?: boolean;
}): React.ReactElement {
  if (services.length === 0) {
    return <ListEmpty filtered={false} entityLabel="サービス情報" />;
  }
  return (
    <div className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {services.map((s) => {
        const tone = toneOf(s.health);
        return (
          <div key={s.name} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3">
            <StatusDot tone={tone === 'default' ? 'muted' : tone} />
            <span className="w-28 shrink-0 font-medium">{s.name}</span>
            <StatusBadge status={s.health} size="sm" />
            <span className="text-xs tabular text-muted-foreground">{s.version ?? '—'}</span>
            <span className="text-xs text-muted-foreground">稼働 {formatUptime(s.uptimeSec)}</span>
            {detailed ? (
              <span className="text-xs text-muted-foreground">
                最終リロード {formatRelative(s.lastReloadAt)}
              </span>
            ) : null}
            {s.message ? (
              <span className="min-w-0 flex-1 truncate text-xs text-warning" title={s.message}>
                {s.message}
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/* ----------------------------------------------------------------- domains */

function DomainsTab({ nodeId }: { nodeId: string }): React.ReactElement {
  const list = useCursorList<DomainSummary>({
    path: `/nodes/${nodeId}/domains`,
    keyFor: (params) => qk.nodeDomains(nodeId, params),
    filters: {},
  });

  const columns: Array<DataTableColumn<DomainSummary>> = [
    {
      id: 'name',
      header: 'ドメイン',
      cell: (d) => (
        <Link
          to="/domains/$domainId"
          params={{ domainId: d.id }}
          search={{ tab: 'overview' }}
          className="font-medium hover:text-primary"
        >
          {d.name}
        </Link>
      ),
    },
    {
      id: 'org',
      header: '契約組織',
      cell: (d) => (
        <Link
          to="/organizations/$orgId"
          params={{ orgId: d.organizationId }}
          search={{ tab: 'overview' }}
          className="hover:text-primary"
        >
          {d.organizationName}
        </Link>
      ),
    },
    { id: 'status', header: '状態', cell: (d) => <StatusBadge status={d.status} size="sm" /> },
    { id: 'mailboxes', header: 'メールボックス', align: 'right', cell: (d) => formatCount(d.mailboxCount) },
  ];

  return (
    <>
      {list.query.isError ? <QueryError error={list.query.error} onRetry={() => void list.query.refetch()} /> : null}
      <DataTable
        columns={columns}
        rows={list.rows}
        rowKey={(d) => d.id}
        loading={list.query.isLoading}
        emptyTitle="このノードに収容されているドメインはありません"
        footer={
          <CursorPagination
            nextCursor={list.nextCursor}
            canGoPrev={list.canGoPrev}
            onNext={list.goNext}
            onPrev={list.goPrev}
            limit={list.limit}
            onLimitChange={list.setLimit}
            currentCount={list.rows.length}
            loading={list.query.isFetching}
          />
        }
      />
    </>
  );
}

/* ----------------------------------------------------------------- metrics */

const RANGES = [
  { value: '1h', label: '1 時間' },
  { value: '24h', label: '24 時間' },
  { value: '7d', label: '7 日' },
  { value: '30d', label: '30 日' },
] as const;

/**
 * 期間 → 時間数。**API 側（`RANGE_HOURS` in `routes/ops/nodes.ts`）と同じ値**にする。
 *
 * 1 時間バケットの系列を「何時間ぶん報告があったか」で評価するのに要る。
 * ここがサーバーとずれると、欠測していないのに欠測しているように見える。
 */
const RANGE_HOURS: Record<'1h' | '24h' | '7d' | '30d', number> = { '1h': 1, '24h': 24, '7d': 168, '30d': 720 };

function MetricsTab({
  nodeId,
  range,
  onRangeChange,
}: {
  nodeId: string;
  range: '1h' | '24h' | '7d' | '30d';
  onRangeChange: (range: '1h' | '24h' | '7d' | '30d') => void;
}): React.ReactElement {
  const query = useQuery({
    queryKey: qk.nodeMetrics(nodeId, range),
    queryFn: () => api.get<NodeMetricsResponse>(`/nodes/${nodeId}/metrics`, { range }),
    refetchInterval: 30_000,
  });

  const points = query.data?.points ?? [];
  /*
   * `hourly` は**報告があった時間だけ**の疎な配列（API が意図して 0 で埋めていない）。
   * そのまま折れ線にすると、エージェントが 6 時間止まっていた区間が
   * 「1 目盛ぶんの隙間」に潰れて平常に見える。ここで期間ぶんの時間グリッドに
   * 敷き直し、報告の無い時間は `null`（＝線を切る）にする。
   */
  const hourly = React.useMemo(
    () => alignHourly(query.data?.hourly ?? [], RANGE_HOURS[range]),
    [query.data?.hourly, range],
  );
  const observedCerts = query.data?.certificates ?? [];

  const mailSeries: Series[] = [
    { key: 'inbound', label: '受信', className: 'stroke-info', points: points.map((p) => ({ at: p.at, value: p.inbound })) },
    { key: 'outbound', label: '送信', className: 'stroke-primary', points: points.map((p) => ({ at: p.at, value: p.outbound })) },
  ];
  /*
   * **スパムはここに混ぜない。** 以前この 1 本は `p.spam` を読んでいたが
   * `points` にその名前は無く、値域が `NaN` になって**拒否の線ごと消えていた**。
   * 出どころ（`usage_samples`）も粒度（1 時間）もハートビートと違うので、
   * 別のグラフにする。
   */
  const rejectSeries: Series[] = [
    { key: 'rejected', label: '拒否', className: 'stroke-destructive', points: points.map((p) => ({ at: p.at, value: p.rejected })) },
  ];
  const threatSeries: Series[] = [
    { key: 'spam', label: 'スパム判定', className: 'stroke-warning', points: hourly.map((p) => ({ at: p.at, value: p.spam })) },
    { key: 'virus', label: 'ウイルス判定', className: 'stroke-destructive', points: hourly.map((p) => ({ at: p.at, value: p.virus })) },
  ];
  const authSeries: Series[] = [
    {
      key: 'authFailure',
      label: '認証失敗',
      className: 'stroke-destructive',
      points: hourly.map((p) => ({ at: p.at, value: p.authFailure })),
    },
    {
      key: 'authSuccess',
      label: '認証成功',
      className: 'stroke-info',
      points: hourly.map((p) => ({ at: p.at, value: p.authSuccess })),
    },
  ];
  const banSeries: Series[] = [
    { key: 'bans', label: 'BAN', className: 'stroke-warning', points: hourly.map((p) => ({ at: p.at, value: p.bans })) },
  ];
  const connSeries: Series[] = [
    { key: 'smtp', label: 'SMTP', className: 'stroke-primary', points: points.map((p) => ({ at: p.at, value: p.smtpConn })) },
    { key: 'imap', label: 'IMAP', className: 'stroke-info', points: points.map((p) => ({ at: p.at, value: p.imapConn })) },
  ];
  const queueSeries: Series[] = [
    { key: 'queue', label: 'キュー長', className: 'stroke-warning', points: points.map((p) => ({ at: p.at, value: p.queueDepth })) },
  ];
  const diskSeries: Series[] = [
    { key: 'disk', label: 'ディスク使用量', className: 'stroke-primary', points: points.map((p) => ({ at: p.at, value: p.diskUsedBytes })) },
  ];

  // 期間の合計。折れ線は「跳ねたか」しか読めないので、規模は数字で並べる。
  const totals = hourly.reduce(
    (acc, p) => ({
      spam: acc.spam + (p.spam ?? 0),
      virus: acc.virus + (p.virus ?? 0),
      authFailure: acc.authFailure + (p.authFailure ?? 0),
      bans: acc.bans + (p.bans ?? 0),
    }),
    { spam: 0, virus: 0, authFailure: 0, bans: 0 },
  );
  const reportedHours = hourly.filter((p) => p.spam !== null).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-xs text-muted-foreground">期間</span>
        <div className="w-32">
          <NativeSelect
            value={range}
            onChange={(v) => onRangeChange(v as '1h' | '24h' | '7d' | '30d')}
            options={RANGES.map((r) => ({ value: r.value, label: r.label }))}
          />
        </div>
        <RefetchingDot active={query.isFetching && !query.isLoading} />
      </div>

      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <StatCard label="スパム判定" value={formatCount(totals.spam)} unit="通" tone={totals.spam > 0 ? 'warning' : 'default'} />
        <StatCard
          label="ウイルス判定"
          value={formatCount(totals.virus)}
          unit="通"
          tone={totals.virus > 0 ? 'destructive' : 'default'}
        />
        <StatCard
          label="認証失敗"
          value={formatCount(totals.authFailure)}
          unit="回"
          tone={totals.authFailure > 0 ? 'warning' : 'default'}
          hint="submission / IMAP の失敗回数"
        />
        <StatCard
          label="BAN"
          value={formatCount(totals.bans)}
          unit="件"
          tone={totals.bans > 0 ? 'warning' : 'default'}
          hint="この期間に netguard が入れた遮断"
        />
      </div>

      <Section title="メール通数">
        <LineChart series={mailSeries} />
      </Section>
      <Section title="拒否">
        <LineChart series={rejectSeries} />
      </Section>
      <Section
        title="スパム・ウイルス判定"
        description={`1 時間ごと。ノードからの報告があったのは ${formatCount(reportedHours)} / ${formatCount(RANGE_HOURS[range])} 時間。報告が無い時間は線を切っています（0 件と区別するため）。`}
      >
        <LineChart series={threatSeries} emptyLabel="この期間の判定報告はありません" />
      </Section>
      <Section
        title="認証（成功・失敗）"
        description="失敗が跳ねているのに成功が平常なら、総当たりを受けています。BAN のグラフと同じ時刻で跳ねているかを見ます。"
      >
        <LineChart series={authSeries} emptyLabel="この期間の認証報告はありません" />
      </Section>
      <Section title="BAN 件数" description="急増は攻撃の一次サイン。現在遮断中の IP は下の一覧にあります。">
        <LineChart series={banSeries} emptyLabel="この期間の BAN はありません" />
      </Section>
      <Section
        title="同時接続数"
        description="ハートビート時点の瞬間値。上限に張り付いていると新規接続が拒否されます。"
      >
        <LineChart series={connSeries} emptyLabel="接続数の報告がありません" />
      </Section>
      <Section title="キュー長">
        <LineChart series={queueSeries} />
      </Section>
      <Section title="ディスク使用量">
        <LineChart series={diskSeries} formatValue={(n) => formatBytes(n)} />
      </Section>

      <NodeObservedCertificates certificates={observedCerts} loading={query.isLoading} />

      <NodeCertificateExpiry nodeId={nodeId} observed={observedCerts} />

      <NodeIpBlocks nodeId={nodeId} />
    </div>
  );
}

/**
 * 疎な `hourly` を**期間ぶんの 1 時間グリッド**へ敷き直す。
 *
 * 報告が無い時間は `null` にして折れ線を切る。0 を入れないのは
 * 「0 件だった」と「エージェントが止まっていた」を同じ形で描かないため
 * （API 側も同じ理由で 0 埋めしていない）。
 */
export function alignHourly(
  rows: NodeMetricHourly[],
  hours: number,
): Array<{ at: string; spam: number | null; virus: number | null; authSuccess: number | null; authFailure: number | null; bans: number | null }> {
  const byHour = new Map(rows.map((r) => [new Date(r.at).setMinutes(0, 0, 0), r]));
  const end = new Date().setMinutes(0, 0, 0);
  const out: Array<{
    at: string;
    spam: number | null;
    virus: number | null;
    authSuccess: number | null;
    authFailure: number | null;
    bans: number | null;
  }> = [];
  for (let i = hours - 1; i >= 0; i -= 1) {
    const at = end - i * 3_600_000;
    const row = byHour.get(at);
    out.push({
      at: new Date(at).toISOString(),
      spam: row?.spam ?? null,
      virus: row?.virus ?? null,
      authSuccess: row?.authSuccess ?? null,
      authFailure: row?.authFailure ?? null,
      bans: row?.bans ?? null,
    });
  }
  return out;
}

/* ------------------------------------- ノード上に実在する証明書（観測値） */

/**
 * ノードのファイルシステムから観測した証明書の残存日数。
 *
 * **CP の台帳（`certificates`）とは別物**で、ズレているのがいちばん危ない
 * （台帳では更新済みなのにノードには古い PEM が置いたまま、が起きる）。
 * 台帳側は下の `NodeCertificateExpiry` が出すので、ここでは
 * 「実際に置かれているもの」だけを並べる。
 */
function NodeObservedCertificates({
  certificates,
  loading,
}: {
  certificates: NodeObservedCertificate[];
  loading: boolean;
}): React.ReactElement {
  const sorted = [...certificates].sort((a, b) => a.daysRemaining - b.daysRemaining);
  return (
    <Section
      title="ノード上の証明書（実ファイルの観測値）"
      description="エージェントが報告した、ノードに実際に置かれている PEM の期限。台帳と食い違っていたら再インストールが要ります。"
    >
      {loading ? (
        <p className="text-sm text-muted-foreground">読み込み中…</p>
      ) : sorted.length === 0 ? (
        <p className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          このノードからの証明書の報告はまだありません（エージェントの
          <code className="mx-1 font-mono">collectMetrics</code>が送ります）。
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {sorted.map((c) => (
            <li key={c.name} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
              <span className="min-w-0 flex-1 truncate font-mono text-xs">{c.name}</span>
              <Badge
                variant={c.daysRemaining < 0 ? 'destructive' : c.daysRemaining <= 14 ? 'warning' : 'success'}
                size="sm"
              >
                {c.daysRemaining < 0 ? `${-c.daysRemaining} 日前に失効` : `残り ${formatCount(c.daysRemaining)} 日`}
              </Badge>
              <span className="text-xs text-muted-foreground">{formatDateTime(c.notAfter)}</span>
              <span className="text-xs text-muted-foreground">観測 {formatRelative(c.observedAt)}</span>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

/* ------------------------------------------------- 証明書の残存日数 */

/**
 * このノードに配置されている TLS 証明書の残存日数。
 *
 * 「メトリクス」に置いているのは、**証明書切れは監視項目**だから
 * （失効すると 587 / 993 が張れず、ハートビートは正常のまま全利用者が使えなくなる。
 * `service_probes` を足した理由と同じ）。
 *
 * **`GET /ops/certificates` はノードで絞れない**（cursor と limit だけ）。
 * 1 ページ引いて画面側で `serverNodeId` を突き合わせる。証明書の総数は
 * 契約ドメイン数と同オーダーなので、200 件で足りない環境ではその旨を出す
 * （黙って一部だけ見せない）。API に `nodeId` が付いたら差し替えること。
 *
 * `observed`（ノード上の実ファイル）を受け取り、**台帳とノードの期限がずれて
 * いる行を明示する**。ズレたまま気付かないのがこの画面でいちばん危ない状態で、
 * 台帳だけを見ていると「更新済み」に見えてしまう。
 */
function NodeCertificateExpiry({
  nodeId,
  observed,
}: {
  nodeId: string;
  observed: NodeObservedCertificate[];
}): React.ReactElement | null {
  const canRead = usePermission(OPS_PERMISSION.certRead);
  const query = useQuery({
    queryKey: qk.certificates({ nodeId, scope: 'node-metrics' }),
    queryFn: () => api.get<{ items: CertificateEntry[]; nextCursor: string | null }>('/certificates', { limit: 200 }),
    enabled: canRead,
  });

  if (!canRead) return null;

  const all = query.data?.items ?? [];
  const mine = all
    .filter((c) => c.serverNodeId === nodeId)
    // 残り日数の短い順。切れそうなものが必ず最初に目に入るようにする。
    .sort((a, b) => (daysUntil(a.notAfter) ?? 1e9) - (daysUntil(b.notAfter) ?? 1e9));
  const truncated = Boolean(query.data?.nextCursor);

  /*
   * ノード上の実ファイルの期限を名前（コモンネーム）で引ける形にする。
   * 名前が一致しない（ノードにその PEM が無い / 台帳に無い名前が置いてある）
   * ケースはどちらも「配布が届いていない」の兆候なので、行ごとに出す。
   */
  const observedByName = new Map(observed.map((c) => [c.name, c]));

  return (
    <Section
      title="証明書の残存日数（CP の台帳）"
      actions={
        <Link to="/certificates" search={{ tab: 'certificates' }} className="text-xs text-primary hover:underline">
          証明書一覧へ
        </Link>
      }
    >
      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <p className="text-sm text-muted-foreground">読み込み中…</p>
      ) : mine.length === 0 ? (
        <p className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          このノードに紐づく証明書はありません。
        </p>
      ) : (
        <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
          {mine.slice(0, 10).map((c) => {
            const days = daysUntil(c.notAfter);
            const onNode = observedByName.get(c.commonName);
            /*
             * 期限が 1 日以上ずれていたら「ノードに届いていない」と読む
             * （秒単位の差は観測時刻の丸めで普通に出るので閾値を置く）。
             * 台帳側の `notAfter` は未発行だと null なので、そのときは比較しない
             * （「一致」とも「ずれ」とも言えないため、下の分岐で未配置扱いになる）。
             */
            const drift =
              onNode !== undefined &&
              c.notAfter !== null &&
              Math.abs(new Date(onNode.notAfter).getTime() - new Date(c.notAfter).getTime()) > 86_400_000;
            return (
              <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
                <span className="min-w-0 flex-1 truncate font-mono text-xs">{c.commonName}</span>
                <StatusBadge status={c.status} size="sm" />
                <Badge
                  variant={days === null ? 'muted' : days < 0 ? 'destructive' : days <= 14 ? 'warning' : 'success'}
                  size="sm"
                >
                  {days === null ? '期限不明' : days < 0 ? `${-days} 日前に失効` : `残り ${days} 日`}
                </Badge>
                {observed.length === 0 ? null : drift ? (
                  <Badge variant="destructive" size="sm">
                    ノード上は {formatDateTime(onNode.notAfter)}
                  </Badge>
                ) : onNode ? (
                  <Badge variant="muted" size="sm">
                    ノードと一致
                  </Badge>
                ) : (
                  <Badge variant="warning" size="sm">
                    ノードに未配置
                  </Badge>
                )}
                <span className="text-xs text-muted-foreground">{formatDateTime(c.notAfter)}</span>
              </li>
            );
          })}
        </ul>
      )}
      {truncated ? (
        <p className="mt-2 text-xs text-warning">
          証明書が 200 件を超えています。ここに出ているのは先頭 200 件に含まれるものだけです（API がノードでの絞り込みに未対応）。
        </p>
      ) : null}
    </Section>
  );
}

/* ------------------------------------------------------------------ config */

function ConfigTab({ node }: { node: NodeDetail }): React.ReactElement {
  const qc = useQueryClient();
  const canCommand = usePermission(OPS_PERMISSION.nodeCommand);

  const query = useQuery({
    queryKey: qk.nodeState(node.id),
    queryFn: () => api.get<unknown>(`/nodes/${node.id}/state`),
  });

  const republish = useMutation({
    mutationFn: () => api.post(`/nodes/${node.id}/commands`, { kind: 'apply.state', args: {} }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: qk.node(node.id) }),
  });

  return (
    <Section
      title={`配布中の desired-state（世代 ${node.desiredGeneration}）`}
      description="ここは読み取り専用。設定はドメインとプランから生成される派生物なので、直接編集しない。秘密値はサーバー側でマスクされて届く。"
      actions={
        canCommand ? (
          <Button variant="outline" size="sm" onClick={() => republish.mutate()} loading={republish.isPending}>
            <RefreshCw aria-hidden />
            再配布する
          </Button>
        ) : null
      }
    >
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data !== undefined ? (
        <JsonBlock value={query.data} maxHeight="34rem" filename={`${node.hostname}-desired-state.json`} />
      ) : null}
    </Section>
  );
}

/* ---------------------------------------------------------------- commands */

function CommandsTab({ nodeId }: { nodeId: string }): React.ReactElement {
  const query = useQuery({
    queryKey: qk.nodeCommands(nodeId),
    queryFn: () => api.get<{ items: NodeCommandRecord[] }>(`/nodes/${nodeId}/commands`),
    refetchInterval: 5_000,
  });

  const columns: Array<DataTableColumn<NodeCommandRecord>> = [
    { id: 'kind', header: 'コマンド', cell: (c) => <code className="font-mono text-xs">{c.kind}</code> },
    { id: 'status', header: '状態', cell: (c) => <StatusBadge status={c.status} size="sm" dot /> },
    { id: 'created', header: '発行', cell: (c) => formatDateTime(c.createdAt), hideBelow: 'md' },
    { id: 'finished', header: '完了', cell: (c) => formatDateTime(c.finishedAt), hideBelow: 'md' },
    {
      id: 'redelivery',
      header: '再配送',
      align: 'right',
      hideBelow: 'lg',
      cell: (c) => (c.redeliveryCount > 0 ? <Badge variant="warning" size="sm">{c.redeliveryCount}</Badge> : '—'),
    },
    {
      id: 'priority',
      header: '優先度',
      align: 'right',
      hideBelow: 'lg',
      cell: (c) => <span className="tabular text-xs text-muted-foreground">{c.priority}</span>,
    },
    {
      id: 'message',
      header: '結果',
      cell: (c) =>
        c.errorMessage ? (
          <span className="text-xs text-destructive">
            {c.errorCode ? `[${c.errorCode}] ` : ''}
            {c.errorMessage}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];

  return (
    <>
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(c) => c.id}
        loading={query.isLoading}
        emptyTitle="コマンド履歴はありません"
      />
    </>
  );
}

function SendCommandButton({ nodeId, hostname }: { nodeId: string; hostname: string }): React.ReactElement {
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [kind, setKind] = React.useState<string>('apply.state');

  const mut = useMutation({
    mutationFn: () => api.post(`/nodes/${nodeId}/commands`, { kind, args: {} }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.nodeCommands(nodeId) });
      setOpen(false);
    },
  });

  // 引数が要るコマンド（cert.install / ip.block / update.apply など）は
  // それぞれの担当画面から発行する。ここは引数なしで安全に撃てるものだけ並べる。
  const simpleKinds = NODE_COMMAND_KIND.filter((k) =>
    ['reload.postfix', 'reload.dovecot', 'reload.rspamd', 'auth.flush', 'apply.state', 'diag.collect'].includes(k),
  );

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Terminal aria-hidden />
        コマンド送信
      </Button>
      <FormDialog
        open={open}
        onOpenChange={setOpen}
        title="ノードコマンドを送信"
        description={`${hostname} に対してコマンドを発行します。実行結果はコマンド履歴に記録されます。`}
        submitLabel="送信する"
        pending={mut.isPending}
        error={mut.isError ? mut.error : undefined}
        onSubmit={() => mut.mutate()}
      >
        <Field label="コマンド" hint="引数が必要なコマンドは、対象の画面（証明書・遮断 IP・アップデート）から発行します。">
          <NativeSelect
            value={kind}
            onChange={setKind}
            options={simpleKinds.map((k) => ({ value: k, label: k }))}
          />
        </Field>
      </FormDialog>
    </>
  );
}

/* ----------------------------------------------------------------- updates */

function UpdatesTab({ node }: { node: NodeDetail }): React.ReactElement {
  const query = useQuery({
    queryKey: [...qk.node(node.id), 'updates'] as const,
    queryFn: () =>
      api.get<{
        items: Array<{
          id: string;
          component: string;
          version: string;
          ring: string;
          status: string;
          appliedAt: string | null;
          message: string | null;
        }>;
      }>(`/nodes/${node.id}/updates`),
  });

  const columns: Array<DataTableColumn<{
    id: string;
    component: string;
    version: string;
    ring: string;
    status: string;
    appliedAt: string | null;
    message: string | null;
  }>> = [
    { id: 'component', header: 'コンポーネント', cell: (r) => r.component },
    { id: 'version', header: 'バージョン', cell: (r) => <span className="tabular">{r.version}</span> },
    { id: 'ring', header: 'リング', cell: (r) => labelOf(r.ring) },
    { id: 'status', header: '結果', cell: (r) => <StatusBadge status={r.status} size="sm" /> },
    { id: 'appliedAt', header: '適用日時', cell: (r) => formatDateTime(r.appliedAt) },
    { id: 'message', header: '備考', cell: (r) => <span className="text-xs text-muted-foreground">{r.message ?? '—'}</span> },
  ];

  return (
    <Section title="アップデート履歴" description={`現在のリング: ${labelOf(node.ring)}`}>
      {query.isError ? <QueryError error={query.error} onRetry={() => void query.refetch()} /> : null}
      <DataTable
        columns={columns}
        rows={query.data?.items ?? []}
        rowKey={(r) => r.id}
        loading={query.isLoading}
        emptyTitle="適用履歴はありません"
      />
    </Section>
  );
}

/* ------------------------------------------------------------ 運用操作 */

/**
 * ノードのライフサイクル操作（A139）。
 *
 * ドレイン → 収容 0 → 退役、という順番でしか消せない（API 側も
 * `allocatedMailboxes > 0` を 400 で弾く）ので、UI もその順に出す。
 * 登録トークンと署名鍵のローテーションは**どちらも「新しい enroll
 * トークンを発行する」**だけ（CP は秘密鍵を持たないので、新しい鍵は
 * ノード側で生成させるしかない）。値は 1 回しか返らない。
 */
function NodeOperationsMenu({ node }: { node: NodeDetail }): React.ReactElement | null {
  const qc = useQueryClient();
  const canWrite = usePermission(OPS_PERMISSION.nodeWrite);
  const canDelete = usePermission(OPS_PERMISSION.nodeDelete);
  const navigate = routeApi.useNavigate();

  const [drainOpen, setDrainOpen] = React.useState(false);
  const [retireOpen, setRetireOpen] = React.useState(false);
  const [approveOpen, setApproveOpen] = React.useState(false);
  const [quarantineOpen, setQuarantineOpen] = React.useState(false);
  const [unquarantineOpen, setUnquarantineOpen] = React.useState(false);
  const [secret, setSecret] = React.useState<NodeEnrollTokenResult | null>(null);
  const [secretTitle, setSecretTitle] = React.useState('登録トークン');

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: qk.node(node.id) });
    void qc.invalidateQueries({ queryKey: ['ops', 'nodes'] });
  };

  const issueToken = useMutation({
    mutationFn: () => api.post<NodeEnrollTokenResult>(`/nodes/${node.id}/enroll-token`, {}),
    onSuccess: (data) => {
      setSecretTitle('登録トークン');
      setSecret(data);
      invalidate();
    },
  });

  const rotateKey = useMutation({
    mutationFn: () => api.post<NodeEnrollTokenResult>(`/nodes/${node.id}/rotate-key`, {}),
    onSuccess: (data) => {
      setSecretTitle('署名鍵のローテーション用トークン');
      setSecret(data);
      invalidate();
    },
  });

  const publishState = useMutation({
    mutationFn: () => api.post<{ generation: number; changed: boolean }>(`/nodes/${node.id}/state/publish`, {}),
    onSuccess: invalidate,
  });

  const verifyDns = useMutation({
    mutationFn: () => api.post(`/nodes/${node.id}/verify-dns`, {}),
  });

  if (!canWrite && !canDelete) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            <Wrench aria-hidden />
            運用操作
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64">
          {canWrite ? (
            <>
              <DropdownMenuItem onSelect={() => publishState.mutate()} disabled={publishState.isPending}>
                <Send className="size-4" aria-hidden />
                desired-state を再発行
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => verifyDns.mutate()} disabled={verifyDns.isPending}>
                <Globe className="size-4" aria-hidden />
                逆引き（PTR / FCrDNS）を再検証
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => issueToken.mutate()} disabled={issueToken.isPending}>
                <KeyRound className="size-4" aria-hidden />
                登録トークンを再発行
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => rotateKey.mutate()} disabled={rotateKey.isPending}>
                <RotateCcw className="size-4" aria-hidden />
                署名鍵をローテーション
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setDrainOpen(true)}>
                <PackageOpen className="size-4" aria-hidden />
                ドレインを開始
              </DropdownMenuItem>
            </>
          ) : null}
          {canDelete ? (
            <>
              <DropdownMenuSeparator />
              {/*
                侵害が疑われたときの緊急手段（`POST /nodes/:id/quarantine`）。
                **退役ではない**（データは残す）が、送信・新規収容・
                desired-state の払い出しを同時に止めて資格情報を失効させる。
                隔離中は「解除」だけを出す（両方並べると誤爆する）。
              */}
              {node.maintenanceMode ? (
                <DropdownMenuItem onSelect={() => setUnquarantineOpen(true)}>
                  <ShieldOff className="size-4" aria-hidden />
                  隔離を解除する
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem className="text-destructive" onSelect={() => setQuarantineOpen(true)}>
                  <ShieldAlert className="size-4" aria-hidden />
                  ノードを隔離する（侵害の疑い）
                </DropdownMenuItem>
              )}
              {node.fingerprintMismatchAt ? (
                <DropdownMenuItem onSelect={() => setApproveOpen(true)}>
                  <Fingerprint className="size-4" aria-hidden />
                  指紋の不一致を承認
                </DropdownMenuItem>
              ) : null}
              <DropdownMenuItem className="text-destructive" onSelect={() => setRetireOpen(true)}>
                <Trash2 className="size-4" aria-hidden />
                ノードを退役させる
              </DropdownMenuItem>
            </>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      <DrainDialog node={node} open={drainOpen} onOpenChange={setDrainOpen} />
      <RetireDialog
        node={node}
        open={retireOpen}
        onOpenChange={setRetireOpen}
        onRetired={() => void navigate({ to: '/nodes', search: {} })}
      />
      <ApproveFingerprintDialog node={node} open={approveOpen} onOpenChange={setApproveOpen} />
      <QuarantineDialog node={node} open={quarantineOpen} onOpenChange={setQuarantineOpen} />
      <UnquarantineDialog node={node} open={unquarantineOpen} onOpenChange={setUnquarantineOpen} />

      <SecretRevealDialog
        open={secret !== null}
        onOpenChange={(next) => {
          if (!next) setSecret(null);
        }}
        title={secretTitle}
        description="エージェントの設定に貼り付けてください。この値はこの画面を閉じると二度と表示できません。"
        items={
          secret
            ? [
                { label: '登録トークン', value: secret.enrollmentToken },
                { label: 'コントロールプレーン URL', value: secret.controlPlaneUrl },
                { label: '有効期限', value: `発行から ${secret.expiresInHours} 時間` },
              ]
            : []
        }
      />
    </>
  );
}

/**
 * 指紋の不一致。**enroll も desired-state の払い出しも止まっている状態**なので、
 * ドリフト帯より上に出す（何をしても適用が進まない理由がこれ）。
 */
function FingerprintBanner({ node }: { node: NodeDetail }): React.ReactElement | null {
  if (!node.fingerprintMismatchAt) return null;
  return (
    <Alert variant="destructive" className="mb-4">
      <AlertTitle>ノード指紋の不一致を検知しています（{formatRelative(node.fingerprintMismatchAt)}）</AlertTitle>
      <AlertDescription>
        このノードへの desired-state の払い出しと再登録が止まっています。
        正当な機械交換（ハードウェア故障・VM の再作成）であれば「運用操作 &gt; 指紋の不一致を承認」から
        理由を書いて解除してください。心当たりが無い場合は<strong>クローンされた VM が
        既存ノードとして再登録を試みている</strong>可能性があります。
      </AlertDescription>
    </Alert>
  );
}

function DrainDialog({
  node,
  open,
  onOpenChange,
}: {
  node: NodeDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const navigate = routeApi.useNavigate();
  const [targetNodeId, setTargetNodeId] = React.useState('');

  const nodes = useQuery({
    queryKey: qk.nodes({ scope: 'drain-target' }),
    queryFn: () => api.get<{ items: ApiNode[] }>('/nodes', { limit: 200 }),
    enabled: open,
  });

  React.useEffect(() => {
    if (open) setTargetNodeId('');
  }, [open]);

  const drain = useMutation({
    mutationFn: () =>
      api.post<{ jobId: string }>(`/nodes/${node.id}/drain`, {
        ...(targetNodeId.length > 0 ? { targetNodeId } : {}),
      }),
    onSuccess: (res) => {
      void qc.invalidateQueries({ queryKey: qk.node(node.id) });
      onOpenChange(false);
      // 202 で provisioning ジョブが返る。進行はそちらでしか追えない。
      void navigate({ to: '/jobs/$jobId', params: { jobId: res.jobId } });
    },
  });

  // 自分自身と、新規割り当てを受け付けないノードは移送先にできない。
  const candidates = (nodes.data?.items ?? []).filter(
    (n) => n.id !== node.id && n.acceptsNewAllocations && n.status !== 'draining',
  );

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="ドレインを開始する"
      description={`${node.hostname} を「新規割り当てを受け付けない」状態にし、収容中のメールボックスを移送します。`}
      submitLabel="ドレインを開始"
      pending={drain.isPending}
      error={drain.error}
      onSubmit={() => drain.mutate()}
    >
      <Alert variant="warning">
        <AlertDescription>
          収容中のメールボックスは {formatCount(node.allocatedMailboxes)} 件です。
          0 になるまでノードは退役できません。進行はプロビジョニングジョブで追跡します。
        </AlertDescription>
      </Alert>

      <Field label="移送先ノード" hint="指定しない場合は配置方式（プールの設定）に従って自動で選ばれます。">
        <NativeSelect
          value={targetNodeId}
          onChange={setTargetNodeId}
          options={[
            { value: '', label: '自動で選ぶ' },
            ...candidates.map((n) => ({ value: n.id, label: `${n.hostname}（${n.region}）` })),
          ]}
        />
      </Field>
    </FormDialog>
  );
}

function RetireDialog({
  node,
  open,
  onOpenChange,
  onRetired,
}: {
  node: NodeDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onRetired: () => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const remove = useMutation({
    mutationFn: () => api.del(`/nodes/${node.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['ops', 'nodes'] });
      onOpenChange(false);
      onRetired();
    },
  });

  const blocked = node.allocatedMailboxes > 0;

  return (
    <ConfirmTextDialog
      open={open}
      onOpenChange={onOpenChange}
      title="ノードを退役させる"
      description="メールボックスが 1 件でも残っているノードは削除できません（配送先が消えます）。"
      confirmText={node.hostname}
      confirmLabel="退役させる"
      pending={remove.isPending}
      error={remove.error}
      onConfirm={() => {
        if (!blocked) remove.mutate();
      }}
    >
      {blocked ? (
        <Alert variant="destructive">
          <AlertDescription>
            まだ {formatCount(node.allocatedMailboxes)} 件のメールボックスを収容しています。
            先にドレインを完了させてください。
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="warning">
          <AlertDescription>
            退役すると <code className="font-mono">decommissioned</code> になり、一覧から消えます。エージェントの接続も拒否されます。
          </AlertDescription>
        </Alert>
      )}
    </ConfirmTextDialog>
  );
}

/**
 * 指紋の承認。**「今提示されている指紋」を新しい正本として焼き付ける**ので、
 * 何を承認するのかを画面に必ず出す（見ないまま押させない）。
 */
function ApproveFingerprintDialog({
  node,
  open,
  onOpenChange,
}: {
  node: NodeDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');

  React.useEffect(() => {
    if (open) setReason('');
  }, [open]);

  const approve = useMutation({
    mutationFn: () =>
      // `.strict()` なので余計なキーを送らない。`fingerprint` は省略すると
      // 「今検知されている指紋」が採用される（画面が古い値を焼き付けない）。
      api.post(`/nodes/${node.id}/approve-fingerprint`, { reason: reason.trim() }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.node(node.id) });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="ノード指紋の不一致を承認する"
      description="承認すると、いま提示されている指紋が新しい正本になります。この操作には直近の再認証が必要です。"
      submitLabel="承認する"
      pending={approve.isPending}
      error={approve.error}
      disabled={reason.trim().length < 10}
      onSubmit={() => approve.mutate()}
      wide
    >
      <KeyValue
        columns={1}
        items={[
          { key: '現在の正本（先頭 12 文字）', value: node.nodeFingerprintPrefix ?? '（未登録）' },
          { key: '提示された指紋', value: node.fingerprintMismatchValue ?? '（記録がありません）' },
          { key: '検知', value: formatDateTime(node.fingerprintMismatchAt) },
        ]}
      />

      <Alert variant="warning">
        <AlertDescription>
          クローンされた VM を承認すると、複製されたノードが正規のノードとして desired-state
          （契約者のメールボックスのパスワードハッシュを含む）を受け取れるようになります。
          <strong>機械交換の事実を確認してから</strong>承認してください。
        </AlertDescription>
      </Alert>

      <Field label="承認する理由" required hint="10 文字以上。監査ログに残ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="ホスト機のマザーボード交換に伴う machine-id の変更（作業記録 OPS-2026-0031）"
        />
      </Field>
    </FormDialog>
  );
}

/* --------------------------------------------------------------- 隔離 */

/**
 * ノードの隔離（`POST /nodes/:nodeId/quarantine`）。
 *
 * **退役ではない。** 侵害が疑われたときに「動いたまま外へ影響を出せない」
 * 状態へ落とす操作で、送信・新規収容・desired-state の払い出しを同時に止め、
 * ノードの資格情報に即時ローテーション期限を打つ。メールボックスの実体は
 * 残るので、証跡を取ってから復旧を判断できる。
 *
 * 資格情報のローテーションを**既定で入れている**のは、盗まれた資格情報が
 * そのまま生きていると隔離した意味が薄いから。外せるようにはしてあるが、
 * 外す理由（＝これから同じ鍵で調査する）を運営が意識して選ぶ形にする。
 */
function QuarantineDialog({
  node,
  open,
  onOpenChange,
}: {
  node: NodeDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  const [rotateCredentials, setRotateCredentials] = React.useState(true);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setRotateCredentials(true);
    }
  }, [open]);

  const quarantine = useMutation({
    // body は `.strict()`。余計なキーを送らない。
    mutationFn: () =>
      api.post<{ rotatedCredentials: number }>(`/nodes/${node.id}/quarantine`, {
        reason: reason.trim(),
        rotateCredentials,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.node(node.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'nodes'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${node.hostname} を隔離する`}
      description="侵害が疑われるノードを、動かしたまま外部へ影響を出せない状態にします。メールボックスの実体は消えません。"
      submitLabel="隔離する"
      pending={quarantine.isPending}
      error={quarantine.error}
      disabled={reason.trim().length < 10}
      onSubmit={() => quarantine.mutate()}
      wide
    >
      <Alert variant="destructive">
        <AlertTitle>この操作で同時に止まるもの</AlertTitle>
        <AlertDescription>
          <ul className="ml-4 list-disc space-y-0.5">
            <li>外向き送信（このノードの契約者はメールを送れなくなります）</li>
            <li>新規ドメイン・メールボックスの収容</li>
            <li>desired-state の払い出し（設定変更が一切届かなくなります）</li>
          </ul>
          受信と IMAP は止まりません。完全に止めるならドレインまたは退役を使います。
        </AlertDescription>
      </Alert>

      <SwitchRow
        label="資格情報を即時ローテーション対象にする"
        description="盗まれた資格情報を無効化します。外すのは「同じ鍵のまま調査を続ける」と決めたときだけにしてください。"
        checked={rotateCredentials}
        onCheckedChange={setRotateCredentials}
      />

      <Field label="隔離する理由" required hint="10 文字以上。監査ログに残ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="不審な外向き接続を検知（SOC-2026-0114）。証跡取得のため隔離。"
        />
      </Field>
    </FormDialog>
  );
}

/**
 * 隔離の解除（`POST /nodes/:nodeId/unquarantine`）。
 *
 * **desired-state の門はここでは開かない。** 開くのは指紋の承認だけ、と
 * API 側が分けている（侵害されたままの機械がワンクリックで復帰しないため）。
 * 画面でもそれを明示し、指紋の門が残っているノードでは
 * 「解除しても設定は届かない」ことを先に伝える。
 */
function UnquarantineDialog({
  node,
  open,
  onOpenChange,
}: {
  node: NodeDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): React.ReactElement {
  const qc = useQueryClient();
  const [reason, setReason] = React.useState('');
  // 既定は false。送信の再開はウォームアップの都合で別途判断する（API の既定と揃える）。
  const [resumeOutbound, setResumeOutbound] = React.useState(false);
  const [acceptsNewAllocations, setAcceptsNewAllocations] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setReason('');
      setResumeOutbound(false);
      setAcceptsNewAllocations(false);
    }
  }, [open]);

  const unquarantine = useMutation({
    mutationFn: () =>
      api.post<{ desiredStateBlocked: boolean }>(`/nodes/${node.id}/unquarantine`, {
        reason: reason.trim(),
        resumeOutbound,
        acceptsNewAllocations,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.node(node.id) });
      void qc.invalidateQueries({ queryKey: ['ops', 'nodes'] });
      onOpenChange(false);
    },
  });

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`${node.hostname} の隔離を解除する`}
      description="運用フラグだけを戻します。何を再開するかは 1 つずつ選んでください。"
      submitLabel="解除する"
      pending={unquarantine.isPending}
      error={unquarantine.error}
      disabled={reason.trim().length < 10}
      onSubmit={() => unquarantine.mutate()}
      wide
    >
      {node.fingerprintMismatchAt ? (
        <Alert variant="warning">
          <AlertTitle>解除しても desired-state は届きません</AlertTitle>
          <AlertDescription>
            このノードは指紋の不一致で隔離の門が残っています。設定を届かせるには
            「運用操作 &gt; 指紋の不一致を承認」が別に要ります（機械そのものを信用し直す操作なので分けてあります）。
          </AlertDescription>
        </Alert>
      ) : null}

      <KeyValue
        columns={1}
        items={[
          { key: '現在の外向き送信', value: node.outboundEnabled ? '許可' : '停止中' },
          { key: '現在の新規収容', value: node.acceptingNewMailboxes ? '受け入れ' : '停止中' },
        ]}
      />

      <SwitchRow
        label="外向き送信を再開する"
        description="長く止めていたノードはレピュテーションが落ちています。再開するならウォームアップの計画とあわせて判断してください。"
        checked={resumeOutbound}
        onCheckedChange={setResumeOutbound}
      />
      <SwitchRow
        label="新規ドメイン・メールボックスの収容を再開する"
        description="切り分けが終わるまでは止めたままにしておくのが安全です。"
        checked={acceptsNewAllocations}
        onCheckedChange={setAcceptsNewAllocations}
      />

      <Field label="解除する理由" required hint="10 文字以上。監査ログに残ります。">
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={3}
          placeholder="調査完了。侵害の痕跡なし（SOC-2026-0114）。監視強化のうえ復帰。"
        />
      </Field>
    </FormDialog>
  );
}

/* ----------------------------------------------------- 遮断中の IP（BAN） */

/**
 * このノードで遮断されている IP（`GET /ops/blocklist?nodeId=`）。
 *
 * BAN 件数は「メトリクス」に置く。**急増は攻撃の一次サインで、時系列の
 * グラフを見る人と同じ人が同じ画面で気付くべきもの**だから。数字だけを
 * セキュリティ画面に隔離すると、ノードの調子を見に来た運用者には届かない。
 *
 * ハートビートには BAN 件数の列が無く、時系列にはできない（netguard の
 * 現在の状態を `ip_blocklist` が持っているだけ）。ここで出せるのは
 * **いまの遮断数と直近の内訳**まで。時系列にするには
 * `node_heartbeats` に列を足すところからになる。
 */
function NodeIpBlocks({ nodeId }: { nodeId: string }): React.ReactElement | null {
  const canRead = usePermission(OPS_PERMISSION.securityRead);
  const query = useQuery({
    queryKey: qk.blocklist({ nodeId, scope: 'node-metrics' }),
    // サーバーの絞りは `nodeId`（`serverNodeId` ではない）。
    queryFn: () =>
      api.get<{ items: BlocklistEntry[]; nextCursor: string | null }>('/blocklist', { nodeId, limit: 50 }),
    enabled: canRead,
    refetchInterval: 60_000,
  });

  if (!canRead) return null;

  const items = query.data?.items ?? [];
  // 期限切れの行も API は返す（履歴として要る）ので、いま効いているものを数える。
  const now = Date.now();
  const active = items.filter(
    (b) => b.action === 'block' && (b.expiresAt === null || new Date(b.expiresAt).getTime() > now),
  );
  const truncated = Boolean(query.data?.nextCursor);

  return (
    <Section
      title="遮断中の IP"
      description="netguard が入れた BAN と運営者が手で入れた遮断。急増は攻撃の一次サインです。"
      actions={
        <Link to="/security" search={{ tab: 'blocks', nodeId }} className="text-xs text-primary hover:underline">
          セキュリティ画面へ
        </Link>
      }
    >
      {query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isLoading ? (
        <p className="text-sm text-muted-foreground">読み込み中…</p>
      ) : active.length === 0 ? (
        <p className="rounded-lg border border-border bg-card px-4 py-3 text-sm text-muted-foreground">
          このノードで遮断中の IP はありません。
        </p>
      ) : (
        <>
          <div className="mb-2 text-sm">
            遮断中 <strong className="tabular">{formatCount(active.length)}</strong> 件
            {truncated ? <span className="ml-1 text-xs text-muted-foreground">（先頭 50 件のうち）</span> : null}
          </div>
          <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
            {active.slice(0, 10).map((b) => (
              <li key={b.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm">
                <code className="min-w-0 flex-1 truncate font-mono text-xs">{b.cidr}</code>
                <Badge variant={b.permanent ? 'destructive' : 'warning'} size="sm">
                  {b.permanent ? '恒久' : '期限つき'}
                </Badge>
                <span className="text-xs text-muted-foreground">{labelOf(b.source)}</span>
                <span className="tabular text-xs text-muted-foreground">
                  {formatCount(b.hitCount)} 回
                </span>
                <span className="text-xs text-muted-foreground">{formatRelative(b.createdAt)}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </Section>
  );
}
